import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Wallet,
  Zap,
  RefreshCw,
  Star,
  Pause,
  BarChart3,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { DesignSubscriptionContactSection } from "@/components/sections/DesignSubscriptionContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { BrandHeritageSection } from "@/components/sections/BrandHeritageSection";
import { UnlimitedDesignsCard } from "@/components/sections/pricing/UnlimitedDesignsCard";
import { WhyUsSection } from "@/components/sections/WhyUsSection";
import { designSubscriptionGrowthItems } from "@/components/sections/WhyUsSection.data";
import { TrustBar } from "@/components/sections/WhyUs/TrustBar";
import { FilterablePortfolio } from "@/components/portfolio/FilterablePortfolio";
import { allProjectsQueryOptions } from "@/lib/portfolio";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";

export const Route = createFileRoute("/design-subscription")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Unlimited Design Subscription — Mind Media" },
      {
        name: "description",
        content:
          "Unlimited design requests for a flat monthly rate. A senior designer on tap, fast turnarounds, pause anytime, no contracts.",
      },
      { property: "og:title", content: "Unlimited Design Subscription — Mind Media" },
      {
        property: "og:description",
        content: "Unlimited design, one flat monthly rate. Pause anytime, cancel anytime.",
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(allProjectsQueryOptions),
  component: DesignSubscriptionPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

function DesignSubscriptionPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-10 sm:pb-14 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:text-5xl">
            <span className="block sm:inline">Unlimited design,</span>{" "}
            <span className={cn("block sm:inline", gradText)}>one flat monthly rate.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-lg text-muted-foreground sm:text-xl">
            A senior designer on tap. Send as many requests as you want, get them back fast,
            <br className="hidden lg:block" />
            and pause anytime. No contracts, no hiring, no surprises.
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              See Pricing <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Book a Call
            </a>
          </div>
        </div>
        <div className="relative mx-auto mt-8 grid grid-cols-2 justify-items-start gap-4 px-6 sm:flex sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-16 sm:gap-y-4">
          {[
            { mobile: "No contracts", desktop: "No contracts" },
            { mobile: "Pause anytime", desktop: "Pause anytime" },
            { mobile: "~48 hour delivery", desktop: "~48 hour delivery" },
            { mobile: "20+ years exp.", desktop: "20+ years experience" },
          ].map((p) => (
            <span key={p.desktop} className="inline-flex items-center gap-2 text-base font-medium">
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
              </span>
              <span className="sm:hidden">{p.mobile}</span>
              <span className="hidden sm:inline">{p.desktop}</span>
            </span>
          ))}
        </div>
      </section>

      <WhyUsSection
        onCtaClick={goToMockup}
        shareloId="9Mg6eNOECMMJen5lyliid"
        shareloTitle="Design subscription — welcome"
        headline={
          <>
            <span className="block sm:inline">Hi I'm Wendel, </span>{" "}
            <span className={cn("block sm:inline", gradText)}>your next designer.</span>
          </>
        }
        growthItems={designSubscriptionGrowthItems}
        showTrustBar={false}
        showRealExperience={false}
        deferVideo
      />

      {/* PORTFOLIO */}
      <FilterablePortfolio sectionId="work" />

      {/* HOW IT WORKS */}
      <section id="how" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="How it works"
            maxWidth="max-w-4xl"
            title={
              <>
                The way design <span className={gradText}>should work.</span>
              </>
            }
          >
            No briefs lost in email, no chasing freelancers, no agency markup. Three simple steps.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                n: 1,
                h: "Subscribe",
                p: "Pick a plan and get access to your own design board. Add as many requests as you'd like.",
              },
              {
                n: 2,
                h: "Request",
                p: "Send over whatever you need, from a landing page to a logo to a set of ads. One at a time, in priority order.",
              },
              {
                n: 3,
                h: "Receive",
                p: "Get your design back in about two business days on average, then revise it until it's exactly right.",
              },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="text-5xl font-bold leading-none tracking-tight text-brand-blue">
                    {s.n}
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight">{s.h}</h3>
                </div>
                <p className="mt-2 text-[15px] text-muted-foreground">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Why a subscription"
            maxWidth="max-w-4xl"
            title={
              <>
                <span className="block md:hidden">
                  <span className="block">This is the part where</span>
                  <span className={cn("block", gradText)}>you'll never go back.</span>
                </span>
                <span className="hidden md:inline">
                  The "<span className={gradText}>you'll never go back</span>"{" "}
                  <span className="text-black">part.</span>
                </span>
              </>
            }
          >
            It replaces unreliable freelancers and expensive agencies with one flat rate and work
            that's actually good.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Wallet,
                h: "One fixed monthly rate",
                p: "No surprise invoices, no per-project haggling. You know exactly what design costs every month.",
              },
              {
                icon: Zap,
                h: "Fast delivery",
                p: "Most requests come back in about two business days, so you're never waiting weeks to ship.",
              },
              {
                icon: RefreshCw,
                h: "Unlimited requests & revisions",
                p: "Queue up as much as you want and revise until it's right. No nickel-and-diming per change.",
              },
              {
                icon: Star,
                h: "Senior-level quality",
                p: "You work with a creative director, not a junior on a template. Two decades of brand and web behind every request.",
              },
              {
                icon: Pause,
                h: "Pause anytime",
                p: "Quiet month? Pause your plan and keep the days you've paid for. Pick back up whenever you're ready.",
              },
              {
                icon: BarChart3,
                h: "Flexible & scalable",
                p: "Busy stretch or a big launch? Scale up. Things slow down? Scale back. It bends around your business.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU CAN REQUEST */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="What you can request"
            maxWidth="max-w-4xl"
            title={
              <>
                Everything you need, <span className={gradText}>under one roof.</span>
              </>
            }
          >
            If it's design, it's on the table. A few of the things people send through their board:
          </SectionHead>
          <div className="mx-auto flex max-w-4xl flex-wrap justify-center gap-3">
            {[
              "Websites",
              "Landing pages",
              "Logos",
              "Brand identity",
              "Social graphics",
              "Ad creative",
              "Email design",
              "Pitch decks",
              "Packaging",
              "UI / UX",
              "Print",
              "Banners",
              "Presentations",
              "and more",
            ].map((c) => (
              <span
                key={c}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <AboutSection onCtaClick={goToMockup} hideMockupButton meetingButtonPrimary />
      <BrandHeritageSection onCtaClick={goToMockup} />

      {/* PRICING — keep the existing Unlimited Designs card */}
      <section id="pricing" className="px-6 pb-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="One simple plan"
            maxWidth="max-w-4xl"
            title={
              <>
                One subscription, <span className={gradText}>endless possibilities.</span>
              </>
            }
          >
            Everything included, billed monthly, cancel or pause whenever you want.
          </SectionHead>
          <UnlimitedDesignsCard onCtaClick={goToMockup} />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="FAQ"
            title={
              <>
                <span className="block sm:inline">The unlimited</span>{" "}
                <span className={cn("block sm:inline", gradText)}>design plan.</span>
              </>
            }
          >
            <span className="block sm:inline">Everything you need to know </span>
            <span className="block sm:inline">about the subscription.</span>
          </SectionHead>
          {(() => {
            const items: [string, string][] = [
              [
                "How fast will I receive my designs?",
                "Most requests come back in about two business days. More complex work, like a full website, takes a bit longer, and we'll tell you the timeline up front.",
              ],
              [
                "How does onboarding work?",
                "After you subscribe, you get your design board and a quick kickoff so we understand your brand and goals. Then you start adding requests right away.",
              ],
              [
                "Who's doing the design?",
                "You work directly with a senior creative director with two decades of brand and web experience. No handing off to juniors, no outsourcing.",
              ],
              [
                "Is there a limit on requests?",
                "Add as many as you like to your board. We work through them one active request at a time, in the order you prioritize, so the most important thing always comes first.",
              ],
              [
                "How does the pause feature work?",
                "If you don't have enough work for a stretch, pause your plan. You keep any days you've already paid for and use them whenever you come back.",
              ],
              [
                "How do you handle larger requests?",
                "Bigger projects like full sites get broken into clear milestones, so you see steady progress instead of waiting in the dark for one big delivery.",
              ],
              [
                "What programs do you design in?",
                "The full professional toolkit, including Figma, Photoshop, Illustrator, and the platforms your site runs on. You get the source files either way.",
              ],
              [
                "Can I cancel anytime?",
                "Yes. No contracts and no lock-in. Cancel whenever, and everything we've made for you is yours to keep.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <DesignSubscriptionContactSection compact />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/design-subscription", label: "Design Subscription" },
          { href: "/branding", label: "Branding" },
          { href: "/get-mockup", label: "Free Mockup" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
  childrenClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
  childrenClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && (
        <p className={cn("mt-4 text-lg leading-relaxed text-muted-foreground", childrenClassName)}>
          {children}
        </p>
      )}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
